import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useUpdateSuperDispatchOrderApiV1ShipmentsShipmentIdSdUpdateOrderPost } from '#/shared/api/generated/shipment/theCargoShipment'
import {
  getApiErrorDetail,
  getHttpStatus,
} from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

import { invalidateShipmentLists } from '#/entities/shipment'

type Translate = ReturnType<typeof useTranslation>['t']

// Map the SD update-order contract's error codes to a message: 502 = SD
// unreachable / rejected the patch, 409 = the shipment has no SD order to update;
// 404 and 422 carry a useful backend `detail` (missing data, SD not connected).
function updateErrorMessage(error: unknown, t: Translate): string {
  const status = getHttpStatus(error)
  if (status === 502) return t('detailSidebar.loadboard.sdUnreachable')
  if (status === 409) return t('detailSidebar.loadboard.updateOrderNoOrder')
  return (
    getApiErrorDetail(error) ?? t('detailSidebar.loadboard.updateOrderError')
  )
}

/**
 * Pushes the shipment's current data to its live Super Dispatch order (DEV-756)
 * via POST /shipments/{id}/sd/update-order — a merge-patch of the existing SD
 * order (price, dates, addresses, payment, vehicles), NOT a re-listing on the
 * board. Used on a dispatched-not-signed SD order. The endpoint reads the
 * shipment row and takes no body, so an edit must be PATCHed to the shipment
 * first, then pushed here.
 */
export function useUpdateSdOrder(shipmentId: string) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const mutation =
    useUpdateSuperDispatchOrderApiV1ShipmentsShipmentIdSdUpdateOrderPost()

  const updateOrder = () => {
    mutation.mutate(
      { shipmentId },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: [`/api/v1/shipments/${shipmentId}`],
          })
          invalidateShipmentLists(queryClient)
          toast.success(t('detailSidebar.loadboard.updateOrderSuccess'))
        },
        onError: (error) => toast.error(updateErrorMessage(error, t)),
      },
    )
  }

  return { updateOrder, isPending: mutation.isPending }
}
